import type {
  QuestPolarisPreview as QuestPolarisPreviewDto,
  QuestPolarisPreviewBadge,
  QuestPolarisPreviewClaimStatus,
  QuestPolarisPreviewRound,
} from '@src/dto/QuestPolarisPreview';
import { isRight } from 'fp-ts/Either';
import {
  BadgeCheck,
  CheckCircle2,
  CircleHelp,
  Coins,
  Lock,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { Button } from '../../components/ui/button';
import { useTRPC } from '../../hooks/useTRPC';
import { cn } from '../../lib/utils';
import { safeLocalStorage } from '../../utils/safeLocalStorage';
import {
  EditorSection,
  inputClassName,
  minimalButtonClassName,
} from './QuestWorkspaceEditorLayout';

type PreviewTab = 'home' | 'badges' | 'allocations' | 'claim';

const tabLabels: Record<PreviewTab, string> = {
  home: 'Home',
  badges: 'Badges',
  allocations: 'Allocations',
  claim: 'Claim',
};
const walletStorageKey = 'quests.preview.walletId';

function formatMonthDay(value: string | null | undefined) {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(date);
}

function formatUpdatedAt(value: string | null | undefined) {
  if (!value) {
    return 'Never';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function formatTokenAmount(value: number) {
  return value.toLocaleString(undefined, { maximumFractionDigits: 9 });
}

function displayValue(value: string | number | null | undefined) {
  return value === null || value === undefined || value === '' ? 'none' : value;
}

function statusText(round: QuestPolarisPreviewRound) {
  if (round.roundStatus === 'IN_PROGRESS') {
    const start = formatMonthDay(round.startsAt);
    const end = formatMonthDay(round.endsAt);
    return start && end ? `${start} - ${end}` : 'IN PROGRESS';
  }

  if (round.roundStatus === 'FINALIZING') {
    return 'FINALIZING';
  }

  if (round.roundStatus === 'COMPLETE') {
    return 'COMPLETE';
  }

  return 'UNSPECIFIED';
}

function earnedBadgeCount(round: QuestPolarisPreviewRound) {
  return round.badges.filter(
    (badge) => badge.level === 'EASY' || badge.level === 'HARD',
  ).length;
}

function claimDescription(claim: QuestPolarisPreviewClaimStatus) {
  if (
    claim.type === 'claimNotAllocated' ||
    claim.type === 'claimIneligibleNoActivity' ||
    claim.type === 'claimIneligibleTokenTransferred'
  ) {
    return 'No allocation';
  }
  if (claim.type === 'claimNotReady') {
    return 'Not ready';
  }
  if (claim.type === 'claimReady') {
    return 'Unclaimed';
  }
  if (claim.type === 'allocationClaimed') {
    return 'Claimed';
  }
  if (claim.type === 'claimClosed') {
    return 'Expired';
  }
  if (claim.type === 'claimError') {
    return claim.errorMessage || '';
  }
  if (claim.type === 'seasonRunning') {
    return 'Season running';
  }

  return '';
}

function BadgePreview(props: { badge: QuestPolarisPreviewBadge }) {
  const earned = props.badge.level === 'EASY' || props.badge.level === 'HARD';
  const hard = props.badge.level === 'HARD';

  return (
    <div className="flex h-20 items-center justify-center">
      <div className="relative flex size-16 items-center justify-center">
        {earned && (
          <>
            <span
              className={cn(
                'absolute inset-1 rounded-full border border-dotted',
                hard ? 'border-white/80' : 'border-pink-400/80',
              )}
            />
            <span
              className={cn(
                'absolute inset-0 rounded-full border border-dotted',
                hard
                  ? 'animate-spin border-orange-400/70 [animation-duration:7s]'
                  : 'border-pink-500/60',
              )}
            />
            <span
              className={cn(
                'absolute -inset-1 rounded-full border border-dotted',
                hard
                  ? 'animate-spin border-fuchsia-400/70 [animation-duration:10s]'
                  : 'border-pink-500/40',
              )}
            />
          </>
        )}
        <div
          className={cn(
            'relative flex size-11 items-center justify-center overflow-hidden rounded-2xl border bg-neutral-900',
            earned
              ? 'border-white/20 shadow-lg shadow-pink-500/20'
              : 'border-neutral-700',
          )}
        >
          {props.badge.imageUrl ? (
            <img
              src={props.badge.imageUrl}
              alt=""
              className={cn(
                'size-full object-cover',
                !earned && 'grayscale opacity-50',
              )}
            />
          ) : props.badge.level === 'HIDDEN' ? (
            <CircleHelp className="size-6 text-neutral-300" />
          ) : (
            <BadgeCheck className="size-6 text-neutral-500" />
          )}
          {props.badge.level === 'LOCKED' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/45">
              <Lock className="size-4 text-neutral-200" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ClaimRow(props: {
  claim: QuestPolarisPreviewClaimStatus;
  onReveal: () => void;
}) {
  const canReveal = props.claim.type === 'claimReady';

  return (
    <div className="flex min-h-14 items-center justify-between gap-3 px-4 py-2">
      <div className="min-w-0">
        <div className="text-sm font-medium text-neutral-300">
          Round {props.claim.season.currentSeason}
        </div>
        <div className="truncate text-xs text-neutral-500">
          {claimDescription(props.claim)}
        </div>
      </div>
      {canReveal ? (
        <button
          type="button"
          onClick={props.onReveal}
          className="rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-neutral-950 transition hover:bg-neutral-200"
        >
          Reveal
        </button>
      ) : props.claim.type === 'claimNotAllocated' ? (
        <span className="text-xs font-medium text-neutral-500">No Claim</span>
      ) : null}
    </div>
  );
}

function PhoneShell(props: { children: ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-[390px] rounded-[2rem] border border-neutral-700 bg-neutral-950 p-2 shadow-2xl shadow-black/40">
      <div className="min-h-[640px] overflow-hidden rounded-[1.5rem] bg-[#f8f7f2] text-neutral-950">
        {props.children}
      </div>
    </div>
  );
}

function HomePreview(props: {
  preview: QuestPolarisPreviewDto;
  round: QuestPolarisPreviewRound;
  onOpenBadges: () => void;
  onOpenAllocations: () => void;
}) {
  const readyClaim = props.preview.homeClaims.some(
    (claim) => claim.type === 'claimReady',
  );

  return (
    <div className="flex min-h-[640px] flex-col bg-[#f8f7f2] p-4">
      <div className="rounded-3xl bg-white p-4 shadow-sm">
        <div className="flex h-[354px] flex-col justify-between overflow-hidden rounded-2xl bg-[radial-gradient(circle_at_70%_15%,#ffe0f1_0,#fff6d9_32%,#f8f7f2_70%)] p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-black tracking-wide text-neutral-950">
              <Sparkles className="size-5 text-pink-500" />
              SUMMER QUEST
            </div>
            <div className="text-right text-[11px] font-semibold uppercase">
              <div className="text-neutral-500">
                Round {props.round.claimStatus.season.currentSeason}
              </div>
              <div className="text-neutral-950">{statusText(props.round)}</div>
            </div>
          </div>

          <div>
            {props.round.roundStatus === 'COMPLETE' && (
              <CheckCircle2 className="mb-2 size-10 text-neutral-950" />
            )}
            <h4 className="line-clamp-2 text-3xl font-semibold leading-tight">
              {props.round.title}
            </h4>
            <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-neutral-500">
              {props.round.subtitle}
            </p>
          </div>

          <div>
            <div className="text-3xl font-semibold">
              {earnedBadgeCount(props.round)} of {props.round.badges.length}
            </div>
            <div className="mt-1 text-sm text-neutral-500">Badges Earned</div>
          </div>
        </div>

        <button
          type="button"
          onClick={props.onOpenBadges}
          className="mt-4 h-12 w-full rounded-full bg-neutral-200 text-sm font-semibold text-neutral-950 transition hover:bg-neutral-300"
        >
          View Badges
        </button>
        {readyClaim && (
          <button
            type="button"
            onClick={props.onOpenAllocations}
            className="mt-3 h-12 w-full rounded-full bg-neutral-950 text-sm font-semibold text-white transition hover:bg-neutral-800"
          >
            Check Available Allocations
          </button>
        )}
      </div>
    </div>
  );
}

function BadgesPreview(props: {
  round: QuestPolarisPreviewRound;
  rounds: QuestPolarisPreviewRound[];
  onSelectRound: (roundNumber: number) => void;
  onOpenClaim: () => void;
}) {
  const ready = props.round.claimStatus.type === 'claimReady';

  return (
    <div className="flex min-h-[640px] flex-col bg-[#f8f7f2]">
      <div className="bg-[radial-gradient(circle_at_78%_8%,#ffd8ef_0,#fff3c4_28%,#f8f7f2_62%)] px-4 pb-5 pt-8">
        <div className="flex items-center gap-2 text-2xl font-black tracking-wide">
          <Sparkles className="size-7 text-pink-500" />
          SUMMER QUEST
        </div>
        <div className="mt-8 text-3xl font-semibold">
          {earnedBadgeCount(props.round)} of {props.round.badges.length} Quests
        </div>
        <div className="mt-2 text-sm font-medium">
          Completed in Round {props.round.roundNumber}
        </div>
        <div className="mt-1 text-xs font-medium text-neutral-500">
          {formatMonthDay(props.round.claimStatus.season.startDate)} -{' '}
          {formatMonthDay(props.round.claimStatus.season.endDate)}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-x-2 gap-y-3 px-4 py-3">
        {props.round.badges.map((badge) => (
          <BadgePreview key={badge.id} badge={badge} />
        ))}
      </div>

      <div className="mt-auto px-4 pb-5">
        <div className="mb-3 flex justify-center gap-1.5">
          {props.rounds.map((round) => (
            <button
              key={round.roundNumber}
              type="button"
              aria-label={`Round ${round.roundNumber}`}
              onClick={() => props.onSelectRound(round.roundNumber)}
              className={cn(
                'size-2 rounded-full',
                round.roundNumber === props.round.roundNumber
                  ? 'bg-neutral-950'
                  : 'bg-neutral-300',
              )}
            />
          ))}
        </div>
        {ready && (
          <>
            <p className="mb-3 text-xs leading-relaxed text-neutral-600">
              By revealing, you agree to the Summer Quest airdrop terms, privacy
              policy, arbitration terms, and staking terms.
            </p>
            <button
              type="button"
              onClick={props.onOpenClaim}
              className="h-12 w-full rounded-full bg-neutral-950 text-sm font-semibold text-white transition hover:bg-neutral-800"
            >
              Reveal
            </button>
          </>
        )}
        {props.round.pageStats.length > 0 && (
          <div className="mt-5 border-t border-neutral-200 pt-4">
            {props.round.pageStats.map((stat) => (
              <div
                key={stat.key}
                className="flex justify-between gap-3 text-sm"
              >
                <span className="text-neutral-500">{stat.label}</span>
                <span className="font-semibold">{stat.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AllocationsPreview(props: {
  claims: QuestPolarisPreviewClaimStatus[];
  onReveal: (claim: QuestPolarisPreviewClaimStatus) => void;
}) {
  return (
    <div className="flex min-h-[640px] items-end bg-black/20">
      <div className="w-full rounded-t-3xl bg-[#f8f7f2] pb-5">
        <div className="flex justify-center py-4">
          <div className="h-1 w-8 rounded-full bg-neutral-300" />
        </div>
        <h4 className="px-5 text-center text-2xl font-semibold">
          Summer Quest Allocations
        </h4>
        <p className="px-5 py-4 text-xs leading-relaxed text-neutral-500">
          By revealing, you agree to the Summer Quest airdrop terms, privacy
          policy, arbitration terms, and staking terms.
        </p>
        <div>
          {props.claims.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-neutral-500">
              No allocations.
            </div>
          ) : (
            props.claims.map((claim) => (
              <ClaimRow
                key={claim.season.currentSeason}
                claim={claim}
                onReveal={() => props.onReveal(claim)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function ClaimPreview(props: { claim: QuestPolarisPreviewClaimStatus }) {
  if (props.claim.type !== 'claimReady') {
    return (
      <div className="flex min-h-[640px] flex-col items-center justify-center bg-[#f8f7f2] p-8 text-center">
        <ShieldCheck className="size-12 text-neutral-300" />
        <h4 className="mt-4 text-xl font-semibold">
          Round {props.claim.season.currentSeason}
        </h4>
        <p className="mt-2 text-sm text-neutral-500">
          {claimDescription(props.claim) || 'No claim preview available.'}
        </p>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-[640px] flex-col bg-[#f8f7f2] p-4">
      <div className="absolute inset-x-0 top-16 flex justify-center opacity-80">
        <div className="size-64 rounded-full border border-dotted border-pink-300" />
      </div>
      <div className="relative z-10 flex flex-1 flex-col items-center justify-center text-center">
        <div className="pb-6 text-sm font-medium">
          Your Round {props.claim.season.currentSeason} Allocation Is
        </div>
        <div className="flex size-20 items-center justify-center rounded-full bg-gradient-to-br from-pink-400 via-orange-300 to-yellow-200 shadow-lg">
          <Coins className="size-10 text-neutral-950" />
        </div>
        <div className="mt-4 text-5xl font-semibold">
          {formatTokenAmount(Math.trunc(props.claim.allocation))}
        </div>
        <div className="mt-1 text-2xl font-semibold">SKR</div>
      </div>
      <div className="relative z-10">
        <p className="pb-4 text-center text-xs font-medium">
          Claim Period Ends On {formatMonthDay(props.claim.claimEndDate)}
        </p>
        <button
          type="button"
          className="h-12 w-full rounded-full bg-neutral-950 text-sm font-semibold text-white"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function ClaimStatusChecks(props: { preview: QuestPolarisPreviewDto }) {
  const claims = props.preview.claimStatusEndpoint.claims;
  const roundNumberById = new Map(
    props.preview.rounds.map((round) => [round.roundId, round.roundNumber]),
  );

  return (
    <div className="space-y-2 rounded-lg border border-neutral-800 bg-neutral-950/30 p-3 text-xs text-neutral-400">
      <div className="flex items-center justify-between gap-3">
        <span className="font-medium uppercase tracking-wide text-neutral-500">
          Claim status checks
        </span>
        <span className="font-mono text-neutral-500">
          {props.preview.claimStatusEndpoint.claimCount}
        </span>
      </div>
      {claims.length === 0 ? (
        <div>No claims returned.</div>
      ) : (
        <div className="space-y-2">
          {claims.map((claim) => (
            <div
              key={claim.id}
              className="rounded-md border border-neutral-800 bg-neutral-950/50 p-2"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium text-neutral-200">
                  Round {roundNumberById.get(claim.roundId) ?? '?'}
                </span>
                <span className="font-mono text-neutral-500">
                  {displayValue(claim.claimState)}
                </span>
              </div>
              <div className="mt-1 grid gap-x-3 gap-y-1 sm:grid-cols-2">
                <span>portal: {claim.status}</span>
                <span>round: {claim.roundStatus}</span>
                <span>amount: {displayValue(claim.claimAmount)}</span>
                <span>opens: {displayValue(claim.claimOpenDate)}</span>
                <span>closes: {displayValue(claim.claimCloseDate)}</span>
                <span>claimed: {displayValue(claim.claimedAt)}</span>
              </div>
              {claim.claimStatusError && (
                <div className="mt-1 text-rose-200">
                  {claim.claimStatusError}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <details>
        <summary className="cursor-pointer text-neutral-300">
          Raw claims response
        </summary>
        <pre className="mt-2 max-h-44 overflow-auto whitespace-pre-wrap rounded-md border border-neutral-800 bg-neutral-950/70 p-2 font-mono text-[11px] leading-relaxed text-neutral-400">
          {JSON.stringify(props.preview.claimStatusEndpoint, null, 2)}
        </pre>
      </details>
    </div>
  );
}

export function QuestPolarisPreview() {
  const trpc = useTRPC();
  const [walletId, setWalletId] = useState(
    () => safeLocalStorage.getItem(walletStorageKey) ?? '',
  );
  const [languageCode, setLanguageCode] = useState('en');
  const [refreshKey, setRefreshKey] = useState(0);
  const [preview, setPreview] = useState<QuestPolarisPreviewDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState<PreviewTab>('home');
  const [selectedRoundNumber, setSelectedRoundNumber] = useState<number | null>(
    null,
  );
  const [selectedClaimRound, setSelectedClaimRound] = useState<number | null>(
    null,
  );

  useEffect(() => {
    const interval = window.setInterval(() => {
      setRefreshKey((value) => value + 1);
    }, 15_000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const value = walletId.trim();
    if (value) {
      safeLocalStorage.setItem(walletStorageKey, value);
    }
  }, [walletId]);

  useEffect(() => {
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      setIsLoading(true);
      try {
        const result = await trpc.getQuestPolarisPreview.query({
          walletId: walletId.trim() || undefined,
          languageCode: languageCode.trim() || undefined,
        });

        if (cancelled) {
          return;
        }

        if (isRight(result)) {
          setPreview(result.right);
          setError(null);
        } else {
          setError(result.left.message || 'Could not load preview.');
        }
      } catch {
        if (!cancelled) {
          setError('Could not load preview.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [trpc, walletId, languageCode, refreshKey]);

  const selectedRound = useMemo(() => {
    if (!preview) {
      return null;
    }

    return (
      preview.rounds.find(
        (round) => round.roundNumber === selectedRoundNumber,
      ) ??
      preview.currentRound ??
      preview.rounds[0] ??
      null
    );
  }, [preview, selectedRoundNumber]);

  const claimForScreen = useMemo(() => {
    if (!preview || !selectedRound) {
      return null;
    }

    return (
      preview.homeClaims.find(
        (claim) => claim.season.currentSeason === selectedClaimRound,
      ) ??
      preview.homeClaims.find((claim) => claim.type === 'claimReady') ??
      selectedRound.claimStatus
    );
  }, [preview, selectedClaimRound, selectedRound]);

  const renderPhone = () => {
    if (!preview || !selectedRound || !claimForScreen) {
      return (
        <PhoneShell>
          <div className="flex min-h-[640px] items-center justify-center bg-[#f8f7f2]">
            {isLoading ? (
              <Loader2 className="size-8 animate-spin text-neutral-400" />
            ) : (
              <span className="text-sm text-neutral-500">No quest rounds.</span>
            )}
          </div>
        </PhoneShell>
      );
    }

    return (
      <PhoneShell>
        {tab === 'home' && (
          <HomePreview
            preview={preview}
            round={selectedRound}
            onOpenBadges={() => setTab('badges')}
            onOpenAllocations={() => setTab('allocations')}
          />
        )}
        {tab === 'badges' && (
          <BadgesPreview
            round={selectedRound}
            rounds={preview.rounds}
            onSelectRound={setSelectedRoundNumber}
            onOpenClaim={() => setTab('claim')}
          />
        )}
        {tab === 'allocations' && (
          <AllocationsPreview
            claims={preview.homeClaims}
            onReveal={(claim) => {
              setSelectedClaimRound(claim.season.currentSeason);
              setTab('claim');
            }}
          />
        )}
        {tab === 'claim' && <ClaimPreview claim={claimForScreen} />}
      </PhoneShell>
    );
  };

  return (
    <EditorSection
      title="Polaris client preview"
      description={
        preview
          ? `${preview.sourceEnvironment} Polaris - updated ${formatUpdatedAt(
              preview.generatedAt,
            )}`
          : undefined
      }
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(minimalButtonClassName, 'shrink-0')}
          onClick={() => setRefreshKey((value) => value + 1)}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <RefreshCw className="size-4" />
          )}
          Refresh
        </Button>
      }
    >
      <div className="grid gap-5">
        <div className="h-[18rem] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="space-y-1 text-xs font-medium text-neutral-300">
              <span>Wallet</span>
              <input
                aria-label="Wallet"
                value={walletId}
                onInput={(event) => setWalletId(event.currentTarget.value)}
                placeholder="wallet address"
                className={inputClassName}
              />
            </label>
            <label className="space-y-1 text-xs font-medium text-neutral-300">
              <span>Locale</span>
              <input
                aria-label="Locale"
                value={languageCode}
                onChange={(event) => setLanguageCode(event.currentTarget.value)}
                placeholder="en"
                className={inputClassName}
              />
            </label>
          </div>

          {preview && <ClaimStatusChecks preview={preview} />}

          <div className="flex flex-wrap gap-2">
            {(Object.keys(tabLabels) as PreviewTab[]).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setTab(item)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  tab === item
                    ? 'border-teal-400/50 bg-teal-500/15 text-teal-200'
                    : 'border-neutral-800 text-neutral-400 hover:border-neutral-700 hover:text-neutral-200',
                )}
              >
                {tabLabels[item]}
              </button>
            ))}
          </div>

          {preview && (
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                Rounds
              </div>
              <div className="grid gap-2">
                {preview.rounds.map((round) => (
                  <button
                    key={round.roundNumber}
                    type="button"
                    onClick={() => setSelectedRoundNumber(round.roundNumber)}
                    className={cn(
                      'flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                      selectedRound?.roundNumber === round.roundNumber
                        ? 'border-teal-500/40 bg-teal-500/10 text-teal-100'
                        : 'border-neutral-800 bg-neutral-950/30 text-neutral-300 hover:border-neutral-700',
                    )}
                  >
                    <span className="min-w-0 truncate">
                      Round {round.roundNumber} - {round.title}
                    </span>
                    <span className="shrink-0 text-xs text-neutral-500">
                      {round.roundStatus}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-950/20 px-3 py-2 text-sm text-rose-200">
              {error}
            </div>
          )}
        </div>

        {renderPhone()}
      </div>
    </EditorSection>
  );
}
